import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import StandardInput from '../../components/ui/StandardInput';
import StandardButton from '../../components/ui/StandardButton';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/zapocni-trgovinu');
    } catch (err) {
      console.error('Login error:', err);
      setError(err?.message || t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Helmet>
        <title>Login - ValueBook</title>
      </Helmet>
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/assets/images/valuebook_marketplace_logo_ispravljen-1766834413050.png"
            alt="ValueBook Marketplace Logo"
            className="h-24 w-auto object-contain mx-auto mb-4"
          />
          <p className="mt-2 text-gray-600">{t('login.subtitle')}</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('login.emailLabel')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
                <StandardInput
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="pl-10"
                  placeholder="vas@email.com"
                  label=""
                  error=""
                  helperText=""
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('login.passwordLabel')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
                <StandardInput
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  label=""
                  error=""
                  helperText=""
                />
              </div>
            </div>

            <StandardButton
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2"
              onClick={handleSubmit}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('login.loading')}
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  {t('login.submitBtn')}
                </>
              )}
            </StandardButton>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="text-teal-700 hover:text-teal-800 font-semibold">
                {t('login.registerLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;